import {
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  Output,
  Input,
  EventEmitter,
} from '@angular/core';
import {
  MotifDropdownModule,
  MotifIconModule,
  MotifProgressBarModule,
} from '@ey-xd/ng-motif';
import {
  IconoirCloudUpload,
  IconoirCheckCircle,
  IconoirDownload,
  IconoirSearch,
  IconoirWarningCircle,
  IconoirMoreHoriz,
} from '@ey-xd/motif-icon';
import { CommonModule, DatePipe } from '@angular/common';
import { ToastService } from 'app/core/service/toast/toast.service';
import { BehaviorSubject, take } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ApiFile } from 'app/models/api-response';
import { FileManipulationService } from 'app/core/service/file-manipulation/file-manipulation.service';
import { DocumentForUploader } from 'app/models/document-for-uploader';

@Component({
  selector: 'app-documents-uploader',
  standalone: true,
  imports: [
    MotifIconModule,
    CommonModule,
    MotifDropdownModule,
    MotifProgressBarModule,
  ],
  templateUrl: './documents-uploader.component.html',
  styleUrl: './documents-uploader.component.scss',
  providers: [DatePipe],
})
export class DocumentsUploaderComponent {
  IconoirCloudUpload = IconoirCloudUpload;
  IconoirCheckCircle = IconoirCheckCircle;
  IconoirDownload = IconoirDownload;
  IconoirSearch = IconoirSearch;
  IconoirWarningCircle = IconoirWarningCircle;
  IconoirMoreHoriz = IconoirMoreHoriz;

  MAX_FILE_SIZE: number = 30 * 1024 * 1024; // 30MB

  @ViewChildren('fileInput') fileInputs!: QueryList<
    ElementRef<HTMLInputElement>
  >;
  @Output() filesEmitter = new EventEmitter<any[]>();
  files: any[] = [];

  // Input variables to control the showing of the warning
  @Input() showWarningFSChecks: boolean = false;
  @Input() showWarningFSvsComp: boolean = false;
  @Input() showWarningCYvsPY: boolean = false;
  //

  private _retrievedFiles!: ApiFile[];

  @Input()
  set retrievedFiles(value: ApiFile[]) {
    this._retrievedFiles = value;
    if (this.retrievedFiles.length > 0) {
      this.setupDocumentsArray();
    }
  }

  get retrievedFiles(): ApiFile[] {
    return this._retrievedFiles;
  }

  isValidPdf$ = new BehaviorSubject<boolean>(false);

  documents: DocumentForUploader[] = [
    {
      documentName: 'Current Year Financial Statements',
      documentNumber: 1,
      accept: 'application/pdf, .doc, .docx',
      uploadDate: '',
      file: undefined,
      hasError: false,
      errorMessage: '',
      succeded: false,
      retrievalLoading: false,
    },
    // {
    //   documentName: "Prior Year Financial Statements",
    //   documentNumber: 2,
    //   accept: 'application/pdf',
    //   uploadDate: "",
    //   file: undefined,
    //   hasError: false,
    //   errorMessage: "",
    //   succeded: false,
    //   retrievalLoading: false,
    // },
    {
      documentName: 'Current Year Tax Computation',
      documentNumber: 3,
      accept: 'application/pdf',
      uploadDate: '',
      file: undefined,
      hasError: false,
      errorMessage: '',
      succeded: false,
      retrievalLoading: false,
    },
    {
      documentName: 'Prior Year Tax Computation',
      documentNumber: 4,
      accept: 'application/pdf',
      uploadDate: '',
      file: undefined,
      hasError: false,
      errorMessage: '',
      succeded: false,
      retrievalLoading: false,
    },
  ];

  // Zoom related properties for breakpoint observer
  isZoomed: boolean = false;

  //flag to avoid toast appearing multiple times
  toastAcknowledged: boolean = false;

  constructor(
    private toastService: ToastService,
    private breakpointObserver: BreakpointObserver,
    private fileManipulationService: FileManipulationService,
    private datePipe: DatePipe
  ) {
    this.breakpointObserver
      .observe([Breakpoints.Handset, '(max-width: 900px)'])
      .subscribe((result) => {
        // result matches returns true if the width is <= 900px
        this.isZoomed = result.matches;
      });
  }

  uploadWithWarning(index: number) {
    this.toastService.clearToastWithTag(true);

    const fileInput = this.fileInputs.toArray()[index];

    if (index === 0) {
      // file for FS
      this.showWarningFSChecks
        ? this.triggerFileUpload(index)
        : fileInput.nativeElement.click();
    }
    if (index === 1) {
      this.showWarningFSvsComp
        ? this.triggerFileUpload(index)
        : fileInput.nativeElement.click();
    }
    if (index == 2) {
      this.showWarningCYvsPY
        ? this.triggerFileUpload(index)
        : fileInput.nativeElement.click();
    }
  }

  triggerFileUpload(index: number): void {
    const fileInput = this.fileInputs.toArray()[index];

    if (this.toastAcknowledged) {
      fileInput.nativeElement.click();
    } else {
      this.toastService
        .showToast('warning', 'You will lost data from past run checks', {
          classTheme: 'motif-theme-light',
          content:
            "If you continue with this process of uploding new data, past data and previous runs or checks will be lost. You won't be able to return to the session where you last left off.",
          actionText: 'Ok, I acknowledge',
          autoHide: false,
        })
        // in this case we consider the .then() case because the toast needs user interaction
        .then((action) => {
          if (action && fileInput) {
            this.toastAcknowledged = true;
            fileInput.nativeElement.click();
          }
        });
    }
  }

  // Handle file selection
  async onFileSelected(
    event: Event,
    document: any,
    index: number
  ): Promise<void> {
    this.toastService.clearToastWithTag(true);

    const input = event.target as HTMLInputElement;
    if (input?.files && input.files.length > 0) {
      const file = input.files[0];

      const isValidFormat = this.isValidFileType(file, index);
      const isValidHeader = await this.validateFileHeaders(file, index);

      if (isValidFormat && isValidHeader && file.size <= this.MAX_FILE_SIZE) {
        document.hasError = false;
        document.succeded = true;
        document.file = file;
        document.uploadDate = this.getUploadDate();

        // Handle file upload logic here
        if (!this.files.includes(document)) {
          this.files.push(document);
        }
        // call the function to validate type and update
        // dropdown according to behavior subject
        this.validateFile(file);
        this.filesEmitter.emit(this.files);
      } else {
        document.succeded = false;
        document.hasError = true;
        document.errorMessage =
          index === 0
            ? `Select a valid format file under ${
                this.MAX_FILE_SIZE / (1024 * 1024)
              }MB.`
            : `Select a valid PDF file under ${
                this.MAX_FILE_SIZE / (1024 * 1024)
              }MB.`;
      }
    }
  }

  private getUploadDate(): string {
    const now = new Date();

    // Format the time to a 12-hour format with am/pm
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const formattedHours = hours % 12 || 12; // Convert 0 to 12 for 12-hour format
    const formattedMinutes = minutes.toString().padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';

    return `Today, at ${formattedHours}:${formattedMinutes} ${period}`;
  }

  // Returns file name without the extension of the file
  getFileName(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');

    // If there's no dot or it's the first character, return the file name as-is
    if (lastDotIndex === -1 || lastDotIndex === 0) {
      return fileName;
    }

    // Return the file name without the extension
    return fileName.substring(0, lastDotIndex);
  }

  toggleDocDropdown(event?: KeyboardEvent | Event) {
    if (event) {
      event.stopPropagation();
    }
  }

  async viewPdf(file: File, index: number) {
    this.toastService.clearToastWithTag(true);

    // Validate MIME type
    const isValidFileType = this.isValidFileType(file, index);
    // Validate file headers
    const isValidHeader = await this.validateFileHeaders(file, index);

    console.log('viewPdf', isValidFileType, isValidHeader, file);

    if (!file) {
      this.toastService.showToast('error', 'No file provided.', {
        classTheme: '',
        autoHide: false,
      });
      return;
    }

    if (!isValidFileType || !isValidHeader) {
      this.toastService.showToast(
        'error',
        'Invalid file. Please provide a valid format for the file.',
        {
          classTheme: '',
          autoHide: false,
        }
      );
      return;
    }

    // Create a temporary URL for the PDF file
    const fileURL = URL.createObjectURL(file);

    // Open the URL in a new browser tab
    window.open(fileURL, '_blank');
    // Cleanup after a short delay to prevent premature revocation
    setTimeout(() => {
      URL.revokeObjectURL(fileURL);
    }, 5000);
  }

  downloadDocument(file: File) {
    this.toastService.clearToastWithTag(true);

    if (!file) {
      this.toastService.showToast('error', 'No file to download', {
        classTheme: '',
        autoHide: false,
      });
      return;
    }
    const fileURL = URL.createObjectURL(file);
    const anchor = document.createElement('a');
    anchor.href = fileURL;
    anchor.download = file.name || 'download.pdf';
    anchor.click();
    URL.revokeObjectURL(fileURL);
  }

  // helper function to determine valid formats by index on documents carousel
  private isValidFileType(file: File, index: number): boolean {
    const allowedTypesStatements = [
      'application/pdf', // pdf
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
    ];
    const allowedExtensionsStatements = ['pdf', 'docx', 'doc'];

    const allowedTypesOthers = ['application/pdf'];
    const allowedExtensionsOthers = ['pdf'];

    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

    return index === 0
      ? allowedTypesStatements.includes(file.type) &&
          allowedExtensionsStatements.includes(fileExtension)
      : allowedTypesOthers.includes(file.type) &&
          allowedExtensionsOthers.includes(fileExtension);
  }

  // Stronger validation than MIME types alone
  // Guaranteed file authenticity so attackers can't spoof a malicious file as pdf
  private async validateFileHeaders(
    file: File,
    index: number
  ): Promise<boolean> {
    // Define magic numbers for different file types
    const pdfMagicNumbers = ['25', '50', '44', '46']; // %PDF
    const docMagicNumbers = ['D0', 'CF', '11', 'E0']; // DOC (Older Word)
    const docxMagicNumbers = ['50', '4B', '03', '04']; // DOCX (ZIP format)

    // Read the first 4 bytes of the file
    const arrayBuffer = await file.slice(0, 4).arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    // Convert bytes to hexadecimal format
    const fileHeader = Array.from(uint8Array).map((byte) =>
      byte.toString(16).toUpperCase().padStart(2, '0')
    );

    // Index 0: Accepts PDF, DOC, DOCX
    if (index === 0) {
      return (
        pdfMagicNumbers.every((val, idx) => fileHeader[idx] === val) ||
        docMagicNumbers.every((val, idx) => fileHeader[idx] === val) ||
        docxMagicNumbers.every((val, idx) => fileHeader[idx] === val)
      );
    }
    // Other indexes: Only accept PDF
    else {
      return pdfMagicNumbers.every((val, idx) => fileHeader[idx] === val);
    }
  }

  // Validate pdf to avoid showing the "view pdf" button if the format is
  // for word documents
  async validateFile(file: File) {
    try {
      const isValid = await this.isPDF(file); // Update observable with result
      this.isValidPdf$.next(isValid);
    } catch (error) {
      console.error('PDF validation failed', error);
      this.isValidPdf$.next(false); // fallback value
    }
  }

  async isPDF(file: File): Promise<boolean> {
    try {
      // First check the MIME type (quick check)
      const validMimeTypes = ['application/pdf'];

      if (!validMimeTypes.includes(file.type)) {
        return false; // Reject immediately if MIME type is wrong
      }

      // Read first 4 bytes of the file
      const arrayBuffer = await file.slice(0, 4).arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      // Convert to hexadecimal
      const fileHeader = Array.from(uint8Array).map((byte) =>
        byte.toString(16).toUpperCase().padStart(2, '0')
      );

      // PDF magic number: %PDF (Hex: 25 50 44 46)
      const pdfMagicNumber = ['25', '50', '44', '46'];

      return fileHeader.join(' ') === pdfMagicNumber.join(' ');
    } catch (error) {
      console.error('Error checking if file is a PDF:', error);
      return false; // Fail-safe fallback
    }
  }

  // Functions to set up the documents in case we retrieved
  // previous uploaded documents from the blob
  private setupDocumentsArray() {
    this.retrievedFiles.forEach((retrievedFile: ApiFile | any) => {
      const documentNumber: number =
        this.determineDocumentByType(retrievedFile);

      const docToSet: DocumentForUploader = this.documents.find(
        (d: DocumentForUploader) => d.documentNumber === documentNumber
      )!;

      docToSet.retrievalLoading = true;

      const fileExtension: string | undefined = retrievedFile
        .filename!.split('.')
        .pop()
        ?.toLowerCase();

      let fileType: string = 'application/pdf';

      if (fileExtension === 'doc') {
        fileType = 'application/msword';
      }
      if (fileExtension === 'docx') {
        fileType =
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      }

      this.fileManipulationService
        .getDownloadFile(retrievedFile.id)
        .pipe(take(1))
        .subscribe({
          next: (data: Blob) => {
            // set variables to document in array
            docToSet.succeded = true;
            docToSet.file = new File([data], retrievedFile.filename!, {
              type: fileType,
            });
            docToSet.uploadDate = this.datePipe.transform(
              `${retrievedFile.uploadedDate}Z`,
              'MMM d, y h:mm a'
            )!;
            this.validateFile(docToSet.file);
            docToSet.retrievalLoading = false;

            // emit files to the home cards to run the processes
            if (!this.files.includes(docToSet)) {
              this.files.push(docToSet);
            }
            this.filesEmitter.emit(this.files);
          },
        });
    });
  }

  private determineDocumentByType(retrievedFile: ApiFile): number {
    if (retrievedFile.type === 50) {
      return 1;
    }
    if (retrievedFile.type === 51) {
      return 3;
    }
    if (retrievedFile.type === 52) {
      return 4;
    }

    return 0;
  }
  // END files retrieval related functions
}
